import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { Title } from '@angular/platform-browser';
import { FilesProvider } from '../../../files/providers/files-provider';
import { CoachesProvider, CoachRow } from '../../providers/coaches-provider';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef, MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Team } from "../../../common/models/team";
import { User } from "../../../common/models/user";
import { Organization } from "../../../common/models/organization";
import { Lang } from "../../../common/models/lang";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AthletesProvider } from "../../../athletes/providers/athletes-provider";
import { LangsProvider } from "../../../common/providers/langs-provider";
import { MatOption } from "@angular/material/autocomplete";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatFormField } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatSelect } from "@angular/material/select";
import { AsyncPipe, NgForOf, NgIf } from "@angular/common";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { map, take } from "rxjs/operators";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";

@Component({
  selector: 'coaches-teams-form',
  providers: [],
  styleUrls: ['./teams.scss'],
  standalone: true,
  imports: [
    MatDialogActions,
    MatDialogContent,
    MatOption,
    MatCheckbox,
    MatFormField,
    MatIcon,
    MatProgressBar,
    MatSelect,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogClose,
    MatDialogTitle,
    NgIf,
    NgForOf,
    MatButton,
    MatIconButton,
    MatTooltip,
    AsyncPipe,
    NgxMatSelectSearchModule
  ],
  templateUrl: './teams.html'
})
export class CoachesTeamsFormComponent implements OnInit, OnDestroy, AfterViewInit {
  public isLoading = false;
  public formTitle = '';

  public availableAthletes: User[] = [];

  public organisation: Organization |null = null;
  public coachId = 0;
  public user: User = new User({});
  public coachRow: CoachRow |null = null;
  public langId: number = Lang.ID_DEFAULT;

  @ViewChild('multiSelect', { static: true }) multiSelect!: MatSelect;

  public availableTeams: Team[] = [];
  public teamsSelected: Team[] = [];
  public teamsSelectFormControl: FormControl<Team[]> = new FormControl<Team[]>([]) as FormControl<Team[]>;
  public teamFilterFormControl: FormControl<string> = new FormControl<string>('') as FormControl<string>;
  public filteredTeams: ReplaySubject<Team[]> = new ReplaySubject<Team[]>(1);
  protected _onDestroy = new Subject<void>();

  public removeCoachAthleteRelations = true;
  constructor(
    public conf: ConfigProvider,
    public currentUser: CurrentUser,
    public utils: UtilsProvider,
    public dialog: MatDialogRef<CoachesTeamsFormComponent>,
    public usersProvider: UsersProvider,
    private _toaster: MatSnackBar,
    private _title: Title,
    private _athletesProvider: AthletesProvider,
    private _router: Router,
    private _route: ActivatedRoute,
    private _coachesProvider: CoachesProvider,
    private _langsProvider: LangsProvider,
    private _translater: TranslateService,
    private _files: FilesProvider,
    @Inject(MAT_DIALOG_DATA) private _data: any
  ) {
    this.availableAthletes = this._data.availableAthletes;
    this.availableTeams = this._data.availableTeams;
    this.organisation = this._data.organization;
    this.formTitle = this._data.formTitle;

    if (this._data.coachRow) {

      this.coachRow = this._data.coachRow;
      this.coachId = this._data.coachRow.coach.id;
      this.user = this._data.coachRow.user;
      this.langId = this._data.coachRow.user.langId;

      this.teamsSelected = this._data.coachRow.teams;
    }
  }

  public ngOnInit() {
    // Charger les coachs filtrés initialement
    this.filteredTeams.next(this.availableTeams.slice());

    // Écouter les changements dans le champ de recherche
    this.teamFilterFormControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterTeams();
      });

    // Initialiser les coachs sélectionnés si nécessaire
    if (this.teamsSelected.length > 0) {
      this.teamsSelectFormControl.setValue(this.teamsSelected);
    }
  }

  public ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  public ngAfterViewInit() {
    this.setInitialValue();
  }

  public onSaveClicked(): void {
    if (this.canSave()) {
      this.isLoading = true;
      this.save().subscribe({
        next: (result: CoachRow) => {
          this._toaster.open(this._translater.instant('Coach_saved'), '', {duration: 5000});
          this.isLoading = false;
          this.dialog.close(result);
        },
        error: (error: any) => this.onError(error)
      });
    } else {
      this.touchFields();
      this._toaster.open(this._translater.instant('Correct_form_errors'), '', {duration: 5000});
    }
  }

  public canSave(): boolean {
    return !this.teamsSelectFormControl.invalid;
  }

  private touchFields(): void {
    this.teamsSelectFormControl.markAsTouched();
  }

  private save(): Observable<CoachRow> {
    const coachRow: CoachRow = {
      user: this.coachRow!.user,
      coach: this.coachRow!.coach,
      athletes: [],
      athleteNames: [],
      teams: [],
      teamNames: [],
      isSuperCoach: false
    };

    if (this.teamsSelectFormControl.value.length) {
      for (let i = 0; i < this.teamsSelectFormControl.value.length; i++) {

        const team = this.utils.findIn(
          this.teamsSelectFormControl.value[i].id,
          this.availableTeams,
          'id');
        if (team) {
          coachRow.teams.push(team);
        }
      }
    }

    return this._coachesProvider.saveCoachTeams(this.organisation?.id!, this.removeCoachAthleteRelations, coachRow).pipe(
      map((row: any) => row)
    );
  }

  protected setInitialValue() {
    this.filteredTeams
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.multiSelect.compareWith = (a: Team, b: Team) => a && b && a.id === b.id;
      });
  }

  protected filterTeams() {
    if (!this.availableTeams) {
      return;
    }

    const search = this.teamFilterFormControl.value?.toLowerCase() || '';
    if (!search) {
      this.filteredTeams.next(this.availableTeams.slice());
      return;
    }

    this.filteredTeams.next(
      this.availableTeams.filter((team) =>
        `${team.name}`.toLowerCase().includes(search)
      )
    );
  }

  private onError(error: any): void {
    this.isLoading = false;

    if (error.status === 422) {
      if (error.error.errors && error.error.errors.email) {
        // c'est qu'on a essayé de faire valider un email test@test par exemple (c'est validé par le validator angular mais pas laravel...)
        return;
      }
    }

    this._toaster.open(this._translater.instant('Error_unknown'), '', {duration: 5000});
  }

}
